import type { HandbookChapter } from '../model';

/**
 * Das Nachschlagekapitel.
 *
 * Es steht bewusst am Ende und ist bewusst nach der **Beobachtung** des
 * Anwenders geordnet ("eine Mail kommt nicht an") und nicht nach der
 * Bauweise des Systems. Wer eine Störung sucht, kennt die Ursache noch
 * nicht -- eine Gliederung nach Modulen hilft ihm nicht.
 */

const stoerungen: HandbookChapter = {
  id: 'was-tun-wenn',
  title: 'Was tun, wenn …',
  summary: 'Häufige Fehlermeldungen und Störungen — und der jeweils nächste Schritt.',
  audience: 'agent',
  sections: [
    {
      id: 'meldungen',
      title: 'Fehlermeldungen',
      blocks: [
        {
          kind: 'table',
          head: ['Meldung', 'Was sie bedeutet', 'Nächster Schritt'],
          rows: [
            [
              'Für diese Aktion fehlt Ihnen die Berechtigung.',
              'Ihre Rolle trägt das nötige Recht nicht.',
              'Kapitel „Rollen und Rechte“ lesen, dann den Administrator fragen.',
            ],
            [
              'Der Datensatz wurde nicht gefunden.',
              'Er gibt es nicht — oder er gehört einem anderen Mandanten.',
              'Prüfen, in welchem Mandanten Sie arbeiten (Kopfleiste rechts).',
            ],
            [
              'Der Datensatz wurde zwischenzeitlich von jemand anderem geändert.',
              'Ein Kollege war schneller.',
              'Neu laden, die Änderung des Kollegen ansehen, dann noch einmal ändern.',
            ],
            [
              'Dieser Statuswechsel ist vom aktuellen Status aus nicht möglich.',
              'Der Weg von hier nach dort ist nicht vorgesehen.',
              'Zwischenschritt gehen, oder den Administrator nach den Statusübergängen fragen.',
            ],
            [
              'Ihre Sitzung ist abgelaufen.',
              'Die Anmeldung ist zu alt.',
              'Neu anmelden. Ungespeicherte Eingaben vorher kopieren.',
            ],
            [
              'Ihr Zugang wurde nach mehreren Fehlversuchen vorübergehend gesperrt.',
              'Zu oft das falsche Kennwort.',
              'Abwarten. Wenn Sie es nicht waren: dem Administrator sagen.',
            ],
            [
              'Der Code aus der Authenticator-App stimmt nicht.',
              'Falscher Code — oder die Uhr des Telefons geht falsch.',
              'Zeitabgleich im Telefon einschalten, dann den nächsten Code nehmen.',
            ],
            [
              'Dieser Code wurde bereits verwendet.',
              'Jeder Code gilt genau einmal.',
              'Auf den nächsten warten.',
            ],
            ['Zu viele Anfragen.', 'Mengenbegrenzung gegriffen.', 'Einen Moment warten.'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          title: 'Die Fehlerkennung',
          text: 'Bei unerwarteten Fehlern zeigt die Oberfläche eine Fehlerkennung. Geben Sie sie an, wenn Sie den Fehler melden — damit findet sich der Vorgang im Protokoll des Servers wieder. Sie ist nicht die Ticketnummer.',
        },
      ],
    },
    {
      id: 'kein-ticket',
      title: 'Eine Mail hat kein Ticket erzeugt',
      blocks: [
        {
          kind: 'steps',
          steps: [
            {
              text: 'Unter Einstellungen → Kanäle → „E-Mail“ nachsehen, wann das Postfach zuletzt abgerufen wurde.',
              detail: 'Steht dort nichts Aktuelles, läuft der Hintergrunddienst nicht.',
            },
            {
              text: '„Jetzt abrufen“ klicken.',
            },
            {
              text: 'Die Filterregeln durchsehen.',
              detail: 'Eine Regel mit „verwirft“ kann die Mail geschluckt haben.',
            },
            {
              text: 'Prüfen, ob die Mail eine automatische Antwort war.',
              detail:
                'Nachrichten mit dem Auto-Submitted-Kopf erzeugen kein Ticket — sonst schriebe sich das System mit dem Abwesenheitsassistenten des Kunden gegenseitig hoch.',
            },
          ],
        },
      ],
    },
    {
      id: 'kunde-hat-nichts',
      title: 'Der Kunde sagt, er hat nichts bekommen',
      blocks: [
        {
          kind: 'steps',
          steps: [
            {
              text: 'Am Ticket prüfen, ob der Beitrag eine „Antwort an den Kunden“ war.',
              detail: 'Eine interne Notiz geht an niemanden. Das ist die häufigste Ursache.',
            },
            {
              text: 'Prüfen, ob der Beitrag noch als Entwurf steht.',
            },
            {
              text: 'Im Verlauf unter dem Beitrag nachsehen, an wen er ging — An, CC, BCC.',
              detail:
                'Stand der Kunde in BCC, ist das kein Fehler: Seine Mail zeigt ihn dann nicht als Empfänger, und er sucht sie womöglich im falschen Ordner.',
            },
            {
              text: 'Unter Einstellungen → Kanäle → „E-Mail“ in die Versandwarteschlange sehen.',
              detail:
                'Dort steht, ob die Nachricht wartet, scheitert oder aufgegeben wurde — mit der Zahl der Versuche.',
            },
            {
              text: 'Am Ansprechpartner prüfen, ob ein Widerspruch nach Art. 21 vermerkt ist.',
              detail: 'Dann verschickt das System bewusst nichts mehr.',
            },
          ],
        },
      ],
    },
    {
      id: 'frist',
      title: 'Die SLA-Uhr rechnet anders als erwartet',
      blocks: [
        {
          kind: 'steps',
          steps: [
            {
              text: 'Prüfen, ob dem Ticket überhaupt eine Zusage zugeordnet ist.',
              detail:
                'Ohne Zusage gibt es keine Frist — und der Bericht zählt das Ticket nicht mit.',
            },
            {
              text: 'Nachsehen, welcher Geschäftszeitenkalender gilt.',
              detail:
                'Die Warteschlange kann einen eigenen tragen; sonst gilt der der Zusage. Eine Bereitschaft rechnet anders als der Tagesdienst.',
            },
            {
              text: 'Prüfen, ob das Ticket zwischenzeitlich in einem anhaltenden Status stand.',
              detail: 'Die Historie zeigt jeden Statuswechsel mit Zeitpunkt.',
            },
            {
              text: 'Feiertage im Kalender prüfen.',
              detail:
                'Bewegliche Feiertage müssen je Jahr eingetragen werden. Ein fehlender Feiertag verkürzt die Frist scheinbar.',
            },
          ],
        },
      ],
    },
    {
      id: 'regel',
      title: 'Eine Regel greift nicht — oder zu oft',
      blocks: [
        {
          kind: 'steps',
          steps: [
            {
              text: '„Testen“ und das Ticket suchen, an dem die Regel greifen sollte — über Nummer oder Betreff.',
              detail:
                'Die Prüfung sagt, welche Bedingung nicht zutrifft und welchen Wert das Ticket wirklich hat.',
            },
            {
              text: 'Prüfen, ob eine frühere Regel „Nach dieser Regel abbrechen“ trägt.',
              detail: 'Dann kommt Ihre gar nicht mehr an die Reihe.',
            },
            {
              text: 'Prüfen, ob die Regel eingeschaltet ist.',
            },
            {
              text: 'Bei zu häufigem Greifen: den Auslöser prüfen.',
              detail:
                '„Ticket geändert“ greift bei jeder Änderung — auch bei der, die die Regel selbst vorgenommen hat.',
            },
          ],
        },
      ],
    },
    {
      id: 'kein-zugang',
      title: 'Ich komme nicht mehr hinein',
      blocks: [
        {
          kind: 'terms',
          items: [
            {
              term: 'Kennwort vergessen',
              text: 'Der Administrator vergibt unter „Benutzer“ ein neues. Es gilt einmalig und wird nirgends gespeichert.',
            },
            {
              term: 'Telefon verloren',
              text: 'Der Administrator setzt den zweiten Faktor zurück. Es gibt keinen anderen Weg — Wiederherstellungscodes gibt es bewusst nicht.',
            },
            {
              term: 'Alles ist gesperrt außer der Einrichtung des zweiten Faktors',
              text: 'Ihre Rolle verlangt ihn. Richten Sie ihn ein, dann erscheint die Navigation wieder.',
            },
            {
              term: 'Die gesuchten Daten sind verschwunden',
              text: 'Fast immer der falsche Mandant. Oben rechts steht, in welchem Sie arbeiten.',
            },
          ],
        },
      ],
    },
    {
      id: 'weiter',
      title: 'Wenn hier nichts steht',
      blocks: [
        {
          kind: 'text',
          text: 'Dieses Handbuch beschreibt die Bedienung. Für den Betrieb — Einrichtung, Sicherung, Wiederherstellung, Aktualisierung des Servers — steht die technische Dokumentation im Projektverzeichnis unter docs/. Bei einem Widerspruch zwischen beiden gilt docs/.',
        },
      ],
    },
  ],
};

export const hilfeChapters: HandbookChapter[] = [stoerungen];
